import { lazy } from "react";
import { Outlet, Route } from "react-router-dom";
import { Toasts, VersionChecker } from "src/components";
import { isDev } from "src/utils/env";
import { pages } from "src/utils/pages";
import { GlobalStyle } from "src/utils/globalStyle";

import PageStoreSummary from "./pages/StoreSummary/PageStoreSummary";
import { PageStoreFixtures } from "./pages/StoreFixtures/PageStoreFixtures";
import { PageMerchflowTemplateBasePogs } from "./pages/MerchflowTemplateBasePogs/PageMerchflowTemplateBasePogs";
import { PageMerchflowTemplates } from "./pages/MerchflowTemplates/PageMerchflowTemplates";
import Page403 from "./pages/page403/Page403";
import Page404 from "./pages/page404/Page404";
import { PageIcons } from "./pages/Icons/PageIcons";
import { IsUnauthorized } from "./components/Authenticator/isUnauthorized";
import { IsAuthorized } from "./components/Authenticator/isAuthorized";
import { Menu } from "./components/Menu/Menu";
import { PageFixtures } from "./pages/Fixtures/PageFixtures";
import { PageFreezer } from "./pages/Freezer/PageFreezer";
import { FeatureFlagProvider } from "./components/FeatureFlagProvider/FeatureFlagProvider";
import { TokenRenewer } from "./components/TokenRenewer/TokenRenewer";
import { AuthTabSync } from "./components/AuthTabSync/AuthTabSync";
import { PageMerchflows_ExportPdf_StorePogId } from "./pages/Merchflows_ExportPdf_StorePogId";
import { PageProjects } from "./pages/Projects/PageProjects";
import { PagePogSets } from "./pages/PogSets/PagePogSets";
import { PageStoreProducts } from "./pages/StoreProducts/PageStoreProducts";
import { PageSettings } from "./pages/Settings/PageSettings";
import { PageSetTokenCallback } from "./pages/SetTokenCallback/PageSetTokenCallback";
import { PageCategories_categoryId_Stores_storeId_Fixtures } from "./pages/PageCategories_categoryId_Stores_storeId_Fixtures/PageCategories_categoryId_Stores_storeId_Fixtures";
import { PageImagine } from "./pages/Imagine/PageImagine";
import { PageImagineShelves } from "./pages/ImagineShelves/PageImagineShelves";
import { PageImagineMacrospace } from "./pages/ImagineMacrospace/PageImagineMacrospace";
import { PageImagineShelvesFixturesGuessing } from "./pages/ImagineShelvesFixturesGuessing/PageImagineShelvesFixturesGuessing";
import { PageCategories } from "./pages/Categories/PageCategories";
import { PageHome } from "./pages/Home/PageHome";
import { PageLogin } from "./pages/Login/PageLogin";
import { PageLogout } from "./pages/Logout/PageLogout";
import { PageMerchflows } from "./pages/Merchflows/PageMerchflows";
import { PageMerchflows_merchflowId_BasePog_basePogId_ } from "./pages/Merchflows_merchflowId_BasePog_basePogId_/PageMerchflows_merchflowId_BasePog_basePogId_";
import { PageMerchflows_merchflowId_S2Templates_templateId_ } from "./pages/Merchflows_merchflowId_S2Templates_templateId_/PageMerchflows_merchflowId_S2Templates_templateId_";
import { PageMerchflows_merchflowId_Stores_storeCode } from "./pages/Merchflows_merchflowId_Stores_storeCode/PageMerchflows_merchflowId_Stores_storeCode";
import { PageMerchflows_merchflowId_Stores_storeCode_Pogs_storePogId_ } from "./pages/Merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_/PageMerchflows_merchflowId_Stores_storeCode_Pogs_storePogId_";
import { PageMerchflows_merchflowId_StoresAll } from "./pages/Merchflows_merchflowId_StoresAll/PageMerchflows_merchflowId_StoresAll";
import { PageMerchflowsPogThumbnail } from "./pages/MerchflowsPogThumbnail/PageMerchflowsPogThumbnail";
import { PagePlayground } from "./pages/Playground/PagePlayground";
import { PageProducts } from "./pages/Products/PageProducts";
import { PageS0 } from "./pages/S0/PageS0";
import { PageS1 } from "./pages/S1/PageS1";
import { PageS2QuadSolutions } from "./pages/S2QuadSolutions/PageS2QuadSolutions";
import { PageS2Solution } from "./pages/S2Solution/PageS2Solution";
import { PageStores } from "./pages/Stores/PageStores";
import { PageUsers } from "./pages/Users/PageUsers";
import { PageProfile } from "./pages/Profile/PageProfile";
import { PageAuditLogs } from "./pages/AuditLogs/PageAuditLogs";
import { PageUpdateReport } from "./pages/UpdateReport/_storePogTriggerId";
import { loadThreeFont } from "./components/Planogram3d/store/utils/three";
const PageControlPanel = lazy(() =>
  import("./pages/ControlPanel/PageControlPanel").then((m) => ({ default: m.PageControlPanel })),
);
const PageMicrospaceAnalysis = lazy(() =>
  import("./pages/MicrospaceAnalysis/PageMicrospaceAnalysis").then((m) => ({
    default: m.PageMicrospaceAnalysis,
  })),
);
const PageAssortmentSimulation = lazy(() =>
  import("./pages/AssortmentSimulation/PageAssortmentSimulation").then((m) => ({
    default: m.PageAssortmentSimulation,
  })),
);
const PageSpacePlanning = lazy(() =>
  import("./pages/SpacePlanning/PageSpacePlanning").then((m) => ({ default: m.PageSpacePlanning })),
);
const PageSpacePlanningReview = lazy(() =>
  import("./pages/SpacePlanning/review/PageSpacePlanningReview").then((m) => ({
    default: m.PageSpacePlanningReview,
  })),
);
const PagePlanograms = lazy(() =>
  import("./pages/Planograms/PagePlanograms").then((m) => ({ default: m.PagePlanograms })),
);
const PagePsaRenderer = lazy(() =>
  import("./pages/PsaRenderer/PsaRendererPage").then((m) => ({ default: m.PsaRendererPage })),
);
const PageAssortmentSimulationDetail = lazy(() =>
  import("./pages/assortment-simulation-detail/PageAssortmentSimulationDetail").then((m) => ({
    default: m.PageAssortmentSimulationDetail,
  })),
);
const PageDistributionPlanner = lazy(() =>
  import("./pages/DistributionPlanner/PageDistributionPlanner").then((m) => ({
    default: m.PageDistributionPlanner,
  })),
);
const PageCDTTreeEditor = lazy(() =>
  import("./pages/CDTTreeEditor/PageCDTTreeEditor").then((m) => ({
    default: m.PageCDTTreeEditor,
  })),
);

const PageStoreViewer = lazy(() =>
  import("./pages/StoreViewer/PageStoreViewer").then((m) => ({ default: m.PageStoreViewer })),
);
const PageCdtVersions = lazy(() =>
  import("./pages/CdtVersions/PageCdtVersions").then((m) => ({ default: m.PageCdtVersions })),
);

const PageCdtStudioConfiguration = lazy(() =>
  import("./pages/CdtStudio/PageCdtStudioConfiguration").then((module) => ({
    default: module.PageCdtStudioConfiguration,
  })),
);
const RootLayout = () => {
  loadThreeFont();

  return (
    <>
      <GlobalStyle />
      <FeatureFlagProvider>
        <Outlet />

        <Menu />
        <Toasts />
        <TokenRenewer />
        <AuthTabSync />
        <VersionChecker />
      </FeatureFlagProvider>
    </>
  );
};

export const appRoutes = (
  <Route element={<RootLayout />}>
    <Route element={<IsUnauthorized />}>
      <Route path={pages.login} element={<PageLogin />} />
      <Route path={pages.setTokenCallback} element={<PageSetTokenCallback />} />
    </Route>

    <Route path={pages.logout} element={<PageLogout />} />

    <Route element={<IsAuthorized />}>
      <Route
        path={pages.merchflows_merchflowId_UpdateReport_storePogTriggerId}
        element={<PageUpdateReport />}
      />

      <Route
        path={pages.imagineShelvesFixturesGuessing_fixturesGuessingId_}
        element={<PageImagineShelvesFixturesGuessing />}
      />
      <Route path={pages.imagineShelvesCategories_categoryId_} element={<PageImagineShelves />} />

      <Route path={pages.imagine} element={<PageImagine />} />
      <Route path={pages.imagineMacrospace} element={<PageImagineMacrospace />} />
      <Route path={pages.imagineAssortment} element={<PageImagineMacrospace />} />

      <Route path={pages.home} element={<PageHome />} />

      <Route path={pages.categories} element={<PageCategories />} />

      <Route
        path={pages.categories_categoryId_cdtStudioConfiguration}
        element={<PageCdtStudioConfiguration />}
      />

      <Route path={pages.categories_categoryId_storeFixtures} element={<PageStoreFixtures />} />

      <Route path={pages.products} element={<PageProducts />} />

      <Route path={pages.fixtures} element={<PageFixtures />} />

      <Route path={pages.projects} element={<PageProjects />} />

      <Route path={pages.projects_projectId_pogsets} element={<PagePogSets />} />

      <Route path={pages.playground} element={<PagePlayground />} />

      <Route path={pages.freezer} element={<PageFreezer />} />

      <Route path={pages.icons} element={<PageIcons />} />

      <Route path={pages.merchflows} element={<PageMerchflows />} />

      <Route path={pages.settings} element={<PageSettings />} />

      <Route path={pages.merchflows_merchflowId_S0_s0UserTriggerId_} element={<PageS0 />} />

      <Route
        path={pages.merchflows_merchflowId_S1_s1UserTriggerId_Templates}
        element={<PageS1 />}
      />

      <Route
        path={pages.merchflows_merchflowId_S1_s1UserTriggerId_Templates_templateId_}
        element={<PageS2QuadSolutions />}
      />

      <Route
        path={pages.merchflows_merchflowId_S2Solution_s2SolutionId_}
        element={<PageS2Solution />}
      />

      <Route
        path={pages.merchflowsPogThumbnail_basePogId_}
        element={<PageMerchflowsPogThumbnail />}
      />

      <Route
        path={pages.merchflowsPogExportPdf_storePogId_}
        element={<PageMerchflows_ExportPdf_StorePogId />}
      />

      <Route
        path={pages.merchflowsPogExportPdf_basePogId_}
        element={<PageMerchflows_ExportPdf_StorePogId />}
      />

      <Route
        path={pages.merchflowsPogThumbnail_storePogId_}
        element={<PageMerchflowsPogThumbnail />}
      />

      <Route
        path={pages.merchflows_merchflowId_S2Templates_templateId_}
        element={<PageMerchflows_merchflowId_S2Templates_templateId_ />}
      />

      <Route
        path={pages.merchflows_merchflowId_BasePog_basePogId_}
        element={<PageMerchflows_merchflowId_BasePog_basePogId_ />}
      />

      <Route
        path={pages.merchflows_merchflowId_StoresAll}
        element={<PageMerchflows_merchflowId_StoresAll />}
      />

      <Route
        path={pages.merchflows_merchflowId_Stores_storeCode_}
        element={<PageMerchflows_merchflowId_Stores_storeCode />}
      />

      <Route
        path={pages.merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_}
        element={<PageMerchflows_merchflowId_Stores_storeCode_Pogs_storePogId_ />}
      />

      <Route path={pages.stores} element={<PageStores />} />
      <Route path={pages.stores_storeId_Products} element={<PageStoreProducts />} />
      <Route path={pages.users} element={<PageUsers />} />
      <Route path={pages.profile} element={<PageProfile />} />
      <Route path={pages.storeSummary} element={<PageStoreSummary />} />
      <Route path={pages.auditLogs} element={<PageAuditLogs />} />

      <Route path={pages.merchflowTemplateBasePogs} element={<PageMerchflowTemplateBasePogs />} />
      <Route path={pages.merchflowTemplates} element={<PageMerchflowTemplates />} />

      <Route
        path={pages.categories_categoryId_stores_storeId_Fixtures}
        element={<PageCategories_categoryId_Stores_storeId_Fixtures />}
      />

      <Route path={pages.controlPanel} element={<PageControlPanel />} />
      <Route path={pages.microspaceAnalysis} element={<PageMicrospaceAnalysis />} />
      <Route path={pages.assortmentSimulation} element={<PageAssortmentSimulation />} />
      <Route path={pages.assortmentSimulationDetail} element={<PageAssortmentSimulationDetail />} />
      <Route path={pages.spacePlanning} element={<PageSpacePlanning />} />
      <Route path={pages.spacePlanningReview} element={<PageSpacePlanningReview />} />
      <Route path={pages.planograms} element={<PagePlanograms />} />
      <Route path={pages.storeViewer_storePogId_} element={<PageStoreViewer />} />
      <Route path={pages.distributionPlanner} element={<PageDistributionPlanner />} />
      <Route path={pages.cdtTreeEditor} element={<PageCDTTreeEditor />} />
      <Route path={pages.categories_categoryId_decisionTrees} element={<PageCdtVersions />} />
      {isDev() && <Route path={pages.psaRenderer} element={<PagePsaRenderer />} />}
    </Route>

    <Route path={pages.accessDenied} element={<Page403 />} />
    <Route path="*" element={<Page404 />} />
  </Route>
);
